import { Tab } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/16/solid";
import {
  Fragment,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useState,
} from "react";

type TabItem = {
  href?: string;
  id: string;
  name: ReactNode;
  onClick?: () => void;
  onClose?: (event: KeyboardEvent | MouseEvent) => void;
};

type Props = {
  currentId?: string;
  items: TabItem[];
  onChange?: (index: number) => void;
};

// TODO: Should we unify the usage of tabs in the project?
export const Tabs = ({ currentId, items, onChange }: Props): ReactElement => {
  const defaultIndex = items.findIndex((item) => item.id === currentId) ?? 0;
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

  const handleChange = (index: number): void => {
    if (index !== selectedIndex) {
      setSelectedIndex(index);
      onChange?.(index);
      const selectedTab = items[index];
      selectedTab.onClick?.();
    }
  };

  const handleClose = (
    event: KeyboardEvent | MouseEvent,
    onClose?: (event: KeyboardEvent | MouseEvent) => void,
  ): void => {
    event.stopPropagation();
    onClose?.(event);
  };

  return (
    <div className="w-full">
      <Tab.Group
        className="h-full"
        onChange={handleChange}
        selectedIndex={selectedIndex}
      >
        <Tab.List className="flex h-full items-center space-x-4">
          {items.map((tab) => (
            <Tab as={Fragment} key={tab.id}>
              {({ selected }) => {
                const WrapperTag = selected ? "div" : tab.href ? "a" : "div";

                return (
                  <WrapperTag
                    {...(!selected && tab.href ? { href: tab.href } : {})}
                    className={`group flex items-center whitespace-nowrap rounded-md px-3 py-2 text-xs outline-none transition-colors duration-200 ${
                      selected
                        ? "bg-zinc-800 text-white"
                        : "cursor-pointer bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800/70"
                    } `}
                  >
                    <span className="max-w-32 select-none truncate">
                      {tab.name}
                    </span>

                    {tab.onClose && (
                      <span
                        aria-label={`Close ${tab.name} tab`}
                        className="ml-2 flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                        onClick={(e: MouseEvent) => handleClose(e, tab.onClose)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleClose(e, tab.onClose);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </span>
                    )}
                  </WrapperTag>
                );
              }}
            </Tab>
          ))}
        </Tab.List>
      </Tab.Group>
    </div>
  );
};
